import { USER_TYPE_ADMIN } from '@solinkcorp/authentication';
import { auth, canUserAccessByTypeRestriction } from '@solinkcorp/vd-common';
import { EventEmitter } from 'events';
import find from 'lodash/find';
import get from 'lodash/get';
import AuthService from 'models/AuthService';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { AuthStore } from 'stores/AuthStore';

let lastUserFeatures = undefined;
let defaultUserFeatures = undefined;
const featuresEvents = new EventEmitter();

/**
 *
 * @param {string} name - Feature name
 * @param {string} legacyName - Legacy name of the feature
 * @param {*} [defaultValue] - Setting default value (used when setting not found)
 * @returns {*}
 */
// Should the default value be set by the client??
function getUserFeature({ name, defaultValue = false, legacyName }) {
  if (!lastUserFeatures) {
    return false;
  }

  const defaultFeatureValue = defaultUserFeatures?.[name] ?? defaultValue;
  const featureDefaultValue =
    legacyName in lastUserFeatures ? lastUserFeatures[legacyName] : defaultFeatureValue;
  return name in lastUserFeatures ? lastUserFeatures[name] : featureDefaultValue;
}

/**
 * @typedef {import('@solinkcorp/authentication').CallHomeUser} CallHomeUser
 * @param {CallHomeUser['features']} newUserFeatures
 */
function updateUserFeatures(newUserFeatures) {
  if (newUserFeatures !== lastUserFeatures) {
    lastUserFeatures = newUserFeatures;
    featuresEvents.emit('change', newUserFeatures);
  }
}

function updateDefaultUserFeatures(newDefaultUserFeatures) {
  defaultUserFeatures = newDefaultUserFeatures;
}
/**
 *
 * @param {string} name - Setting name
 * @param {*} [defaultValue] - Setting default value (used when setting not found)
 * @returns {*}
 */
const getUserSetting = function getUserSetting({ name, defaultValue = false }) {
  const user = AuthStore.getUser() || {};

  return get(user.app_metadata, [name], defaultValue);
};

/**
 * Check with the user has a Solink email address.
 * @returns {boolean}
 */
const hasSolinkEmail = (user = AuthStore.getUser()) => {
  const { email } = user || {};
  return auth.isSolinkEmail(email);
};

export default {
  updateUserFeatures,
  updateDefaultUserFeatures,
  hasCurrentDayData() {
    return getUserFeature({ name: 'currentDayData' });
  },

  canViewClipsPage() {
    return (
      !AuthService.isGuest &&
      getUserFeature({ name: 'clips', defaultValue: true }) &&
      !this.canAccessAlarmStatusOnly() &&
      !this.isLiveViewOnly()
    );
  },

  /** This only control if a user can access the Events page, not if user can see events at all. */
  canViewEventsPage() {
    return (
      !AuthService.isGuest &&
      !this.hasNoFullAccessInKiosk() &&
      getUserFeature({ name: 'events', defaultValue: true }) &&
      !this.canAccessAlarmStatusOnly() &&
      !this.isLiveViewOnly()
    );
  },

  canViewSettings() {
    return !AuthService.isGuest && !this.hasNoFullAccessInKiosk();
  },

  canViewDashboardPage() {
    return (
      !AuthService.isGuest &&
      getUserFeature({ name: 'homePage' }) &&
      !this.hasNoFullAccessInKiosk() &&
      !this.canAccessAlarmStatusOnly() &&
      !this.isLiveViewOnly()
    );
  },

  /** This only control if a user can access the Video page, not if user can watch video at all. */
  canViewVideoPage() {
    return (
      getUserFeature({ name: 'video', defaultValue: true }) && !this.canAccessAlarmStatusOnly()
    );
  },

  canViewDiscoverPage() {
    return (
      getUserFeature({ name: 'dashboards' }) &&
      !this.hasNoFullAccessInKiosk() &&
      !AuthService.isGuest &&
      !this.canAccessAlarmStatusOnly() &&
      !this.isLiveViewOnly()
    );
  },

  canViewReferral() {
    return !AuthService.isGuest && !isKiosk();
  },

  canViewHelpCenter() {
    return !isKiosk();
  },

  shouldMonitorPerformance() {
    return getUserFeature({ name: 'performanceMonitoring', defaultValue: true });
  },

  isImpersonating() {
    return AuthService.isImpersonating;
  },

  /**
   * @deprecated - Use userSession.isSolinkUser instead.
   */
  isSolinkUser(user) {
    // TODO when email check removed in future, use getUserSetting instead
    const userToCheck = user || AuthStore.getUser();
    return userToCheck && hasSolinkEmail(userToCheck);
  },

  _getUserType() {
    const { userType } = AuthStore.getUser() || {};

    return userType;
  },

  isAdminUser() {
    return this._getUserType() === USER_TYPE_ADMIN;
  },

  canShareToCloud() {
    return (
      !AuthService.isGuest && (this.hasPrivateSharesEnabled() || this.hasPublicSharesEnabled())
    );
  },

  canCancelDownload() {
    return !this.isCloudOnly();
  },

  canAccessAudio() {
    return getUserFeature({ name: 'audio', defaultValue: true });
  },

  canDownload() {
    return getUserFeature({ name: 'downloads', defaultValue: true });
  },

  canAccessRevenueReporting() {
    return getUserFeature({ name: 'revenueReporting' });
  },

  isCloudOnly() {
    return getUserSetting({ name: 'cloudOnly' });
  },

  useMultiviewForSingle() {
    return getUserSetting({ name: 'useMultiviewForSingle' });
  },

  isDemoMode() {
    const user = AuthStore.getUser() || {};
    return user && user.app_metadata && user.app_metadata.isDemoEnabled;
  },

  webHlsMp4LiveEnabled() {
    return getUserFeature({ name: 'webHlsMp4Live' });
  },

  shouldPlayHlsMp4Live(stream) {
    if (stream) {
      return this.webHlsMp4LiveEnabled() && stream.isMp4Enabled;
    }
    return this.webHlsMp4LiveEnabled();
  },
  /**
   * Check whether the user has access to the SMS real-time notifications feature.
   *
   * @returns {boolean}
   */
  isSMSNotificationsEnabled() {
    return getUserFeature({ name: 'smsNotifications' });
  },

  /**
   * Check whether the user has access to email notifications feature.
   *
   * @returns {boolean}
   */
  isEmailNotificationsEnabled() {
    return getUserFeature({ name: 'emailNotifications', defaultValue: true });
  },

  canModifyCameraAnnotations() {
    return this.isAdminUser();
  },

  canModifyMotionZones() {
    return this.isAdminUser();
  },

  canModifyMotionDetectionType() {
    return this.isAdminUser() && getUserFeature({ name: 'aiComputerVision' });
  },

  canModifyPtzSettings() {
    return getUserFeature({ name: 'ptzEnableControl' });
  },

  canControlPtzCamera() {
    return getUserFeature({ name: 'ptzLiveControl' });
  },

  canConfigureDwellTime() {
    // FIXME: can this react to the lack of `enable_multiple_rois` in the ai settings dwell time module?
    // NOTE: this would require to fetch merged settings on load,
    // which may be more work than we're comfortable throwing at the server
    const isDwellTimeEnabled = true; //get(
    //   targetLocation,
    //   'connectSettings.ai.dwell_time.enabled',
    //   false,
    // );
    return (
      this.isAdminUser() && getUserFeature({ name: 'aiDwellTimeDetection' }) && isDwellTimeEnabled
    );
  },

  canConfigureDwellTimeZones() {
    // FIXME: can this react to the lack of `enable_multiple_rois` in the ai settings dwell time module?
    // NOTE: this would require to fetch merged settings on load,
    // which may be more work than we're comfortable throwing at the server
    const isDwellTimeMultipleRoisEnabled = true; //get(
    //   targetLocation,
    //   'connectSettings.ai.dwell_time.enable_multiple_rois',
    //   false,
    // );
    return this.canConfigureDwellTime() && isDwellTimeMultipleRoisEnabled;
  },

  showAllVehicleDwellTimeZoneOptions() {
    return getUserFeature({ name: 'showAllVehicleDwellTimeZoneOptions', defaultValue: false });
  },

  isClipDurationRestricted() {
    return (
      getUserFeature({ name: 'restrictClipDuration', defaultValue: true }) ||
      this.hasNoFullAccessInKiosk()
    );
  },

  hasManageFloorPlans() {
    return getUserFeature({ name: 'manageFloorPlans', defaultValue: this.isAdminUser() });
  },

  /** View floor plans. `manageFloorPlans` alone is enough to see layouts. */
  canSeeFloorplan() {
    if (this.isLiveViewOnly()) return false;
    return getUserFeature({ name: 'floorPlan' }) || this.hasManageFloorPlans();
  },

  /** Upload/edit floor plans. Does not require the separate `floorPlan` view flag. */
  canManageFloorplan() {
    if (this.isLiveViewOnly()) return false;
    return this.hasManageFloorPlans();
  },

  /**
   * Check whether enable full function access in Kiosk mode
   */
  hasFullAccessInKiosk() {
    return getUserFeature({ name: 'kioskDataEnabled' });
  },

  /**
   * Check whether user has no full access to the app in Kiosk mode
   */
  hasNoFullAccessInKiosk() {
    return isKiosk() && !this.hasFullAccessInKiosk();
  },

  /**
   * Check if user can create private clips
   */
  hasPrivateSharesEnabled() {
    return getUserFeature({ name: 'privateSharesEnabled', defaultValue: false });
  },

  /**
   * Check if user can create and archive public clips
   */
  hasPublicSharesEnabled() {
    return getUserFeature({ name: 'publicSharesEnabled', defaultValue: true });
  },

  /**
   * Check if user has the sharedCameraGroupsEnabled user feature enabled
   */
  hasSharedCameraGroupsEnabled() {
    return getUserFeature({ name: 'sharedCameraGroupsEnabled' });
  },

  /**
   * Check if user can share the clips to others
   */
  canShareClips() {
    return getUserFeature({ name: 'shareClipsEnabled', defaultValue: true });
  },

  /**
   * Check if user can create shared camera groups.
   * Managing existing shared groups uses CameraUtil.canManageSharedCameraGroup.
   */
  canCreateSharedCameraGroups() {
    if (this.isLiveViewOnly()) return false;
    return this.hasSharedCameraGroupsEnabled();
  },

  /**
   * Get locations user is restricted to
   */
  getRestrictedLocations() {
    return getUserSetting({ name: 'devices', defaultValue: [] });
  },

  /**
   * Get location groups user is restricted to
   */
  getRestrictedLocationGroups() {
    return getUserSetting({ name: 'groups', defaultValue: [] });
  },

  /**
   * Check if user has the sharedCameraGroupsAccess user feature enabled
   */
  canAccessSharedCameraGroups() {
    return getUserFeature({ name: 'sharedCameraGroupsVisibility', defaultValue: true });
  },

  /**
   * Check if user has the webAccess user feature enabled
   * If the alarm access only is set to true, we would take that as web access is NOT allowed
   */
  canAccessWeb() {
    return getUserFeature({ name: 'webAccess', defaultValue: true });
  },

  /**
   * If user only has access to manage alarm status.
   * This flag ignores the ones that allow user to access the pages other than the Alarms page.
   * If this flag is true, the user can only access the Alarms page and nothing else.
   * But if the user also have `manageAlarmSettings` feature enabled, they can manage all the alarm settings.
   * @deprecated This function is deprecated.
   * Please use `useUserHasFeature` hook from `@solinkcorp/authentication` instead, which internally utilizes the `userHasFeature()` function.
   */
  canAccessAlarmStatusOnly() {
    return getUserFeature({ name: 'alarmAccessOnly', defaultValue: false });
  },

  canRenameCameras() {
    return getUserFeature({ name: 'renameCameras', defaultValue: true }) && !this.isLiveViewOnly();
  },

  canPlayLiveInKiosk() {
    return getUserFeature({ name: 'kioskModeLive', defaultValue: true });
  },

  /**
   * Check if the user is delivery only
   */
  isDeliveryOnly() {
    return getUserFeature({ name: 'deliveryOnly', defaultValue: false });
  },

  /**
   * Check if the user is restricted to live camera feeds only.
   * Live-view-only users can watch live streams but have no access to
   * recorded footage, timeline scrubbing, or historical event review.
   */
  isLiveViewOnly() {
    return getUserFeature({ name: 'liveViewOnly', defaultValue: false });
  },

  isClipsFillGapsEnabled() {
    return getUserFeature({ name: 'clipsFillGapsEnabled', defaultValue: false });
  },

  /**
   * Register a listener which is called when one or more user features change.
   *
   * The listener is, by default, called immediately when registered.
   */
  subscribe(listener, callImmediately = true) {
    featuresEvents.addListener('change', listener);
    if (callImmediately) {
      listener();
    }
  },

  unsubscribe(listener) {
    featuresEvents.removeListener('change', listener);
  },

  /** Checks if user's userType matches userTypeRestriction */
  isVisibleByUserType(userTypeRestriction) {
    const currentUser = AuthStore.getUser() || {};
    const isImpersonating = this.isImpersonating();

    return canUserAccessByTypeRestriction({
      user: currentUser,
      userTypeRestriction,
      isImpersonating,
    });
  },

  /**
   * Check whether the current user can view the feature standard tab with the provided visibility restrictions
   * @param {UserType | undefined} options.userType required user type
   * @param {string | undefined} options.userFeature required feature the user must have to view the tab
   * @param {{any?: string[], all?: string[]} | undefined} options.userFeatures {any?: array, all?: array} this object can contain an any property,
   * an array of the features that any of the features can allow the user to view the tab,
   * or an all property, an array of the features that are all required for the user to view the tab.
   * @returns boolean, true if the tab should be visible
   */
  canViewFeatureStandardTab({ userType, userFeature, userFeatures }) {
    const userTypeMatch = this.isVisibleByUserType(userType);

    let userFeatureMatch;
    if (!userFeature && !userFeatures) {
      userFeatureMatch = true;
    } else {
      let singleUserFeatureMatch;
      let anyUserFeaturesMatch;
      let allUserFeaturesMatch;
      if (userFeature) {
        singleUserFeatureMatch = getUserFeature({ name: userFeature, defaultValue: false });
      }
      if (userFeatures?.any) {
        const noneFeatureMatch = !find(userFeatures.any, (feature) => {
          return getUserFeature({ name: feature, defaultValue: false });
        });
        anyUserFeaturesMatch = !noneFeatureMatch;
      }
      if (userFeatures?.all) {
        const hasFeatureNotMatch = find(userFeatures.any, (feature) => {
          return !getUserFeature({ name: feature, defaultValue: false });
        });
        allUserFeaturesMatch = !hasFeatureNotMatch;
      }
      userFeatureMatch = singleUserFeatureMatch || anyUserFeaturesMatch || allUserFeaturesMatch;
    }
    return userTypeMatch && userFeatureMatch;
  },

  /**
   * Check whether the current user can view the event field with the provided visibility restrictions
   * @param {string | boolean} visibility required user type.
   *  Supported values include 'admin' and 'standard', as well as the special cases
   *  'solink' (a user with a solink email address) and 'impersonator' (an impersonated user)
   * @returns boolean, true if the tab should be visible
   */
  canViewEventField(visibility) {
    // eventField visibility type: true | false | solink | admin | standard | connect | impersonator
    // https://github.com/SolinkCorp/event-standard/blob/edb2373895af4de4663dba1cf585d2acbb28caf3/generator/definitions/eventStandard/README.md?plain=1#L74
    if (typeof visibility === 'boolean') {
      return visibility;
    }

    return this.isVisibleByUserType(visibility);
  },

  /**
   * Check if the user can manage user features through VD
   */
  canManageUserFeatures() {
    return getUserFeature({ name: 'manageUserFeatures', defaultValue: false });
  },

  /**
   * Check if the user can create real time widgets
   */
  canCreateRealTimeWidgets() {
    return getUserFeature({ name: 'realTimeWidgets', defaultValue: false });
  },

  canManageCameraLinking() {
    return getUserFeature({ name: 'manageCameraLinks', defaultValue: false });
  },

  canManageEvents() {
    return getUserFeature({ name: 'canManageEvents', defaultValue: false });
  },

  viewCameraLinksVisualization() {
    return getUserFeature({ name: 'viewCameraLinksVisualization', defaultValue: false });
  },

  reduceBandwidthUsage() {
    return getUserFeature({ name: 'reduceBandwidthUsage', defaultValue: false });
  },

  logAPIRequestToMixpanel() {
    return getUserFeature({ name: 'logAPIRequestToMixpanel', defaultValue: false });
  },

  userHasAccessToAllLocations(user) {
    const hasProfileRestrictions =
      !!user?.profile?.locationIds?.length || !!user?.profile?.locationGroupIds?.length;
    return !hasProfileRestrictions;
  },

  canModifyBlockedExitRoi() {
    return getUserFeature({ name: 'modifyBlockedExitRoi', defaultValue: false });
  },

  canLaunchVideoWall() {
    return getUserFeature({ name: 'videoWall', defaultValue: true });
  },

  /**
   * Managing Spot Check requires clientSettings flag and user feature flag
   * Currently 'canCreateScheduledSpotChecks' is the feature flag name in event standard,
   * but it controls access to both Scheduled and Event Spot Checks
   * @param {CallHomeClientSettings} clientSettings
   * @param {Boolean} iSolinkUser
   * @returns {boolean}
   */
  canCreateScheduledAndEventSpotChecks(clientSettings, iSolinkUser = false) {
    return (
      !!clientSettings.scheduledSpotCheckEnabled &&
      (getUserFeature({ name: 'canCreateScheduledSpotChecks', defaultValue: false }) || iSolinkUser)
    );
  },

  /**
   * Managing Spot Check requires user feature flag
   * @param {Boolean} isSolinkUser
   * @returns {boolean}
   */
  canCreateSpotChecks(isSolinkUser = false) {
    return getUserFeature({ name: 'canCreateSpotChecks', defaultValue: false }) || isSolinkUser;
  },

  canManageRiskFlag(isSolinkUser = false) {
    return isSolinkUser && getUserFeature({ name: 'manageRSARules', defaultValue: false });
  },

  hasDeveloperAccess() {
    return getUserFeature({ name: 'developer', defaultValue: false });
  },

  cloudVideoStorage() {
    return getUserFeature({ name: 'viewCloudStorageSettings', defaultValue: false });
  },

  faceblurEnabled(userSession) {
    return (
      getUserFeature({ name: 'accessToFaceBlur', defaultValue: false }) &&
      userSession.clientSettings.features?.includes('faceblur')
    );
  },

  canManageEventFlows() {
    return getUserFeature({ name: 'manageEventWorkflows', defaultValue: false });
  },
  canViewAlarmEvents() {
    return (
      !AuthService.isGuest &&
      ((getUserFeature({ name: 'alarmEventsVisibility', defaultValue: false }) &&
        !this.canAccessAlarmStatusOnly()) ||
        this.canManageAlarmSettings())
    );
  },
  canManageAccessControl() {
    return getUserFeature({ name: 'canManageAccessControl', defaultValue: false });
  },
};
